import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Star, Clock, Users, Headphones, Shield, Award, Globe, Zap, CheckCircle, TrendingUp, Package, Warehouse, Phone, Truck, MapPin, BarChart3 } from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';
import { companyInfo, statistics, partners, services, testimonials, awards, heroImages } from '../data/mock';
import logo from '../assets/images/logo.png';
import lepusTruck from "../assets/images/lepus-group-truck.jpeg";

// Refined animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const StatCard = ({ stat, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  const IconComponent = {
    Clock, Users, Headphones, Shield, Award, Globe, Truck, BarChart3
  }[stat.icon] || Truck;

  return (
    <motion.div
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#1d526d]/20 h-full group-hover:-translate-y-1">
        <div className="flex items-start justify-between mb-4">
          <motion.div 
            className="w-12 h-12 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-xl flex items-center justify-center shadow-md"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <IconComponent className="w-6 h-6 text-white" />
          </motion.div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              <AnimatedCounter
                number={stat.number}
                display={stat.display}
                suffix={stat.suffix || ''}
                precision={stat.precision || 0}
                duration={2}
              />
            </div>
            <div className="text-[#1d526d] font-medium text-sm">{stat.label}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ServiceCard = ({ service, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const IconComponent = {
    Package, Globe, Users, Warehouse, Zap, Truck
  }[service.icon] || Truck;

  return (
    <motion.div
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#1d526d]/20 h-full flex flex-col group hover:-translate-y-1">
        <motion.div 
          className="w-12 h-12 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-xl flex items-center justify-center mb-4 shadow-md group-hover:shadow-lg"
          whileHover={{ scale: 1.1, rotate: 8 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <IconComponent className="w-6 h-6 text-white" />
        </motion.div>
        
        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1d526d] transition-colors duration-300">
          {service.title}
        </h3>
        
        <p className="text-gray-600 mb-4 leading-relaxed flex-grow">
          {service.shortDescription}
        </p>
        
        <div className="space-y-2 mb-4">
          {service.features.slice(0, 3).map((feature, idx) => (
            <div key={idx} className="flex items-start text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-[#1d526d] mr-2 flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>
        
        <motion.div
          whileHover={{ x: 4 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="mt-auto"
        >
          <Link
            to="/contact" 
            className="inline-flex items-center text-[#1d526d] font-semibold hover:text-[#2a6b89] transition-colors"
          >
            Get Quote <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

const TestimonialCard = ({ testimonial, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      variants={scaleIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col">
        <div className="flex items-center mb-4">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
          ))}
        </div>
        
        <blockquote className="text-gray-700 mb-6 leading-relaxed italic flex-grow">
          "{testimonial.content}"
        </blockquote>
        
        <div className="border-t border-gray-100 pt-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-bold text-gray-900">{testimonial.name}</div>
              <div className="text-[#1d526d] font-semibold text-sm">{testimonial.title}</div>
              <div className="text-gray-600 text-sm">{testimonial.company}</div>
            </div>
            <div className="text-right text-xs text-gray-500">
              <div className="font-semibold text-[#1d526d]">{testimonial.shipments}</div>
              <div>Partnership: {testimonial.partnership}</div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Home = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Trust-Focused Design */}
      <section className="relative min-h-screen bg-gradient-to-br from-gray-50 to-white overflow-hidden pt-20">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-40">
          <img 
            src={heroImages.primary}
            alt="Professional Transportation Services" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/95"></div>
        </div>

        {/* Clean Geometric Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#1d526d]/5 to-transparent rounded-full blur-2xl"
          />
          <motion.div
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-br from-gray-200/20 to-transparent rounded-full blur-xl"
          />
        </div>

        <div ref={heroRef} className="relative z-10 min-h-screen flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Content - Professional Approach */}
              <motion.div
                variants={slideIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="space-y-8"
              >
                {/* Trust Indicators */}
                <motion.div
                  variants={fadeIn}
                  className="flex items-center space-x-4 mb-6"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 rounded-lg overflow-hidden shadow-md border border-gray-200">
                      <img 
                        src={logo} 
                        alt="Lepus Group Inc" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{companyInfo.name}</div>
                      <div className="text-xs text-[#1d526d] font-semibold tracking-wide">SINCE 2006</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-[#1d526d] mr-1" />
                      <span>Licensed</span>
                    </div>
                    <div className="flex items-center">
                      <Award className="w-4 h-4 text-[#1d526d] mr-1" />
                      <span>Certified</span>
                    </div>
                  </div>
                </motion.div>

                {/* Main Headline - Balanced Size */}
                <motion.h1
                  variants={fadeIn}
                  className="text-4xl lg:text-5xl font-bold leading-tight text-gray-900"
                >
                  Reliable Transportation
                  <span className="block text-[#1d526d] mt-2">You Can Trust</span>
                </motion.h1>

                {/* Value Proposition */}
                <motion.p
                  variants={fadeIn}
                  className="text-xl text-gray-600 leading-relaxed max-w-2xl"
                >
                  18+ years of excellence in freight transportation across North America. 
                  <strong className="text-gray-900"> On-time delivery guaranteed</strong> with 
                  <strong className="text-gray-900"> zero damage policy</strong>.
                </motion.p>

                {/* Key Stats Row */}
                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  animate={heroInView ? "visible" : "hidden"}
                  className="grid grid-cols-3 gap-6 py-6 border-y border-gray-200"
                >
                  {[
                    { number: 98.7, suffix: "%", label: "On-Time Rate" },
                    { number: 15000, suffix: "+", label: "Deliveries" },
                    { number: 24, suffix: "/7", label: "Support" }
                  ].map((stat, index) => (
                    <motion.div key={index} variants={fadeIn} className="text-center">
                      <div className="text-2xl lg:text-3xl font-bold text-[#1d526d] mb-1">
                        <AnimatedCounter 
                          number={stat.number} 
                          suffix={stat.suffix} 
                          precision={stat.number === 98.7 ? 1 : 0}
                          duration={2} 
                        />
                      </div>
                      <div className="text-gray-600 text-sm font-medium">{stat.label}</div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* CTA Buttons - Trust-Building */}
                <motion.div
                  variants={fadeIn}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Link 
                      to="/contact" 
                      className="bg-[#1d526d] hover:bg-[#2a6b89] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl group"
                    >
                      Get Free Quote
                      <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                  
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <a 
                      href={`tel:${companyInfo.phoneRaw}`}
                      className="border-2 border-[#1d526d] text-[#1d526d] hover:bg-[#1d526d] hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center"
                    >
                      <Phone className="w-5 h-5 mr-3" />
                      Call Now
                    </a>
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Right Content - Professional Image */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={heroInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={lepusTruck}
                    alt="Lepus Group Professional Transportation Fleet"
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  
                  {/* Trust Badge */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-lg flex items-center justify-center">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">100% Insured</div>
                        <div className="text-sm text-gray-600">$2M Coverage</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section - Clean & Trustworthy */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Proven Performance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our track record speaks for itself. See why industry leaders choose Lepus Group 
              for their most critical transportation needs.
            </p>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.2 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {statistics.map((stat, index) => (
              <StatCard key={stat.id} stat={stat} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section - Modern Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Complete Transportation Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From full truckload to specialized logistics, we provide comprehensive transportation 
              services backed by cutting-edge technology and experienced professionals.
            </p>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.slice(0, 6).map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.5 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mt-16"
          >
            <Link
              to="/services"
              className="bg-[#1d526d] hover:bg-[#2a6b89] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center group"
            >
              View All Services
              <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trust Section - Industry Partners */}
      <section className="py-20 bg-[#1d526d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Building long-term partnerships through consistent excellence, reliability, 
              and innovative transportation solutions across North America.
            </p>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {partners.map((partner, index) => (
              <motion.div
                key={partner.id}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 border border-white/20"
              >
                <div className="text-center">
                  <h3 className="text-xl font-bold text-white mb-3">{partner.name}</h3>
                  <p className="text-blue-100 mb-4 text-sm leading-relaxed">{partner.description}</p>
                  <div className="border-t border-white/20 pt-4">
                    <div className="text-white font-bold text-lg mb-1">{partner.loads_per_year}</div>
                    <div className="text-blue-200 text-xs mb-2">Annual Loads</div>
                    <div className="text-blue-100 text-sm font-semibold">
                      Partnership: {partner.partnership_duration}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section - Social Proof */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              What Our Partners Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Real experiences from industry professionals who trust Lepus Group 
              with their critical transportation and logistics needs.
            </p>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Trust & Action */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-[#1d526d] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={heroImages.logistics}
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Subtle animated elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-12 leading-relaxed max-w-3xl mx-auto">
              Join thousands of satisfied clients who trust Lepus Group with their transportation needs. 
              Get your free quote today and experience the difference of working with true professionals.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/contact" 
                  className="bg-white text-[#1d526d] px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg inline-flex items-center justify-center group"
                >
                  Get Free Quote
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="border-2 border-white text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-[#1d526d] transition-all duration-300 inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-3" />
                  Call {companyInfo.phone}
                </a>
              </motion.div>
            </div>

            {/* Awards & Certifications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, threshold: 0.5 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="pt-12 border-t border-white/20"
            >
              <h3 className="text-2xl font-bold text-white mb-8">Industry Recognition</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {awards.map((award, index) => (
                  <motion.div
                    key={award.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/15 transition-all duration-300 border border-white/20"
                  >
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-white font-bold text-sm mb-1">{award.title}</div>
                    <div className="text-gray-200 text-xs mb-1">{award.organization}</div>
                    <div className="text-gray-300 text-xs font-semibold">{award.year}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// This is the key fix - make sure you have the default export
export default Home;